import React, { useEffect, useRef } from 'react';
import { ExternalLink, Radio } from 'lucide-react';
import { Button, Card, EmptyState, Pill, Skeleton, Spinner } from '../../../components/ui';
import type { SignalStatus, SignalView } from '../../../types/monitoring';
import {
  CATEGORY_LABEL,
  formatDate,
  hostOf,
  SIGNAL_STATUS_LABEL,
  signalStatusTone,
  timeAgo,
} from '../format';
import { MetaLine, SentimentMark, SeverityPill, SubjectLink } from './ui';

const TRIAGE: Array<{ status: SignalStatus; label: string }> = [
  { status: 'reviewed', label: 'Reviewed' },
  { status: 'dismissed', label: 'Dismiss' },
  { status: 'escalated', label: 'Escalate' },
];

export const SignalRow: React.FC<{
  signal: SignalView;
  busy: boolean;
  showSubject?: boolean;
  onStatus: (id: string, status: SignalStatus) => void;
}> = ({ signal, busy, showSubject = true, onStatus }) => (
  <article className={`px-4 py-3 ${signal.status === 'dismissed' ? 'opacity-55' : ''}`}>
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <SeverityPill severity={signal.severity} />
      <Pill tone="outline">{CATEGORY_LABEL[signal.category]}</Pill>
      {signal.status !== 'new' && <Pill tone={signalStatusTone(signal.status)}>{SIGNAL_STATUS_LABEL[signal.status]}</Pill>}
      <span
        className="ml-auto text-[12px] text-neutral-400"
        title={`Detected ${new Date(signal.detectedAt).toLocaleString()}`}
      >
        {timeAgo(signal.detectedAt)}
      </span>
    </div>

    {signal.url ? (
      <a
        href={signal.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1.5 inline-flex items-start gap-1 text-[14px] leading-snug text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
      >
        <span className="min-w-0">{signal.title}</span>
        <ExternalLink className="mt-1 h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
      </a>
    ) : (
      <p className="mt-1.5 text-[14px] leading-snug text-ink">{signal.title}</p>
    )}

    {signal.summary && <p className="mt-1 text-[13px] leading-relaxed text-neutral-500">{signal.summary}</p>}

    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
      {showSubject && <SubjectLink subject={signal.subject} />}
      <MetaLine parts={[signal.source || hostOf(signal.url), formatDate(signal.publishedAt || signal.detectedAt)]} />
      <SentimentMark value={signal.sentiment} />
    </div>

    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
      {TRIAGE.map(({ status, label }) => {
        const current = signal.status === status;
        return (
          <Button
            key={status}
            size="xs"
            variant={current ? 'secondary' : 'ghost'}
            disabled={busy}
            aria-pressed={current}
            onClick={() => onStatus(signal.id, current ? 'new' : status)}
            className={current ? 'ring-neutral-300' : ''}
          >
            {label}
          </Button>
        );
      })}
    </div>
  </article>
);

export interface SignalFeedProps {
  signals: SignalView[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  busyId: string | null;
  onStatus: (id: string, status: SignalStatus) => void;
  onLoadMore: () => void;
  showSubject?: boolean;
  /** Replaces the list when something is wrong */
  notice?: React.ReactNode;
  emptyTitle?: string;
  emptyAction?: React.ReactNode;
}

export const SignalFeed: React.FC<SignalFeedProps> = ({
  signals,
  loading,
  loadingMore,
  hasMore,
  busyId,
  onStatus,
  onLoadMore,
  showSubject = true,
  notice,
  emptyTitle = 'No signals yet',
  emptyAction,
}) => {
  const sentinel = useRef<HTMLDivElement>(null);
  // Keep the newest callback without re-creating the observer on every render
  const loadMore = useRef(onLoadMore);
  loadMore.current = onLoadMore;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore.current();
      },
      { rootMargin: '240px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, signals.length]);

  if (loading) {
    return (
      <Card divided aria-busy="true" aria-label="Loading signals">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-2 px-4 py-3.5">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </Card>
    );
  }

  if (notice) return <Card className="p-3">{notice}</Card>;

  if (signals.length === 0) {
    return (
      <Card>
        <EmptyState icon={Radio} title={emptyTitle} action={emptyAction} />
      </Card>
    );
  }

  return (
    <>
      <Card divided>
        {signals.map((signal) => (
          <SignalRow key={signal.id} signal={signal} busy={busyId === signal.id} showSubject={showSubject} onStatus={onStatus} />
        ))}
      </Card>
      {/* The button is also the scroll sentinel: reaching it loads the next page on its own */}
      {hasMore && (
        <div ref={sentinel} className="mt-3 flex justify-center">
          <Button size="sm" variant="secondary" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? <Spinner /> : null}
            {loadingMore ? 'Loading' : 'Load more'}
          </Button>
        </div>
      )}
    </>
  );
};
